/**
 * Send Change Order Modal
 *
 * Modal for sending a change order to the client for approval via email.
 * Includes options for custom message, PDF attachment, and CC recipients.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, Send, Paperclip, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { useSendToClientMutation } from '../../services/changeOrderApi';
import { useToast } from '../../contexts/ToastContext';
import type { ChangeOrder, SendToClientRequest } from '../../types/changeOrder';

interface SendChangeOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  changeOrder: ChangeOrder;
  projectId: string;
  onSuccess?: () => void;
}

// Form validation schema
const sendChangeOrderSchema = yup.object({
  recipientEmail: yup
    .string()
    .email('Please enter a valid email address')
    .required('Recipient email is required'),
  cc: yup.string().optional(),
  subject: yup.string().max(200, 'Subject cannot exceed 200 characters').optional(),
  message: yup.string().max(2000, 'Message cannot exceed 2000 characters').optional(),
  includeAttachments: yup.boolean().default(true),
});

type SendFormData = yup.InferType<typeof sendChangeOrderSchema>;

export function SendChangeOrderModal({
  isOpen,
  onClose,
  changeOrder,
  projectId,
  onSuccess,
}: SendChangeOrderModalProps) {
  const toast = useToast();
  const [sendToClient, { isLoading }] = useSendToClientMutation();
  const [sendSuccess, setSendSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SendFormData>({
    resolver: yupResolver(sendChangeOrderSchema) as Resolver<SendFormData>,
    defaultValues: {
      recipientEmail: '',
      cc: '',
      subject: `Change Order ${changeOrder.coNumber} - ${changeOrder.title}`,
      message: '',
      includeAttachments: true,
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSendSuccess(false);
      reset({
        recipientEmail: '',
        cc: '',
        subject: `Change Order ${changeOrder.coNumber} - ${changeOrder.title}`,
        message: '',
        includeAttachments: true,
      });
    }
  }, [isOpen, changeOrder, reset]);

  const onSubmit = async (data: SendFormData) => {
    try {
      // Parse CC emails
      const ccEmails = data.cc
        ? data.cc
            .split(/[,;\s]+/)
            .map(email => email.trim())
            .filter(email => email.length > 0)
        : [];

      const sendData: SendToClientRequest = {
        recipientEmail: data.recipientEmail,
        cc: ccEmails.length > 0 ? ccEmails : undefined,
        subject: data.subject,
        message: data.message,
        includeAttachments: data.includeAttachments,
      };

      await sendToClient({
        projectId,
        changeOrderId: changeOrder._id,
        data: sendData,
      }).unwrap();

      setSendSuccess(true);
      toast.success('Change order sent to client successfully');

      // Close after a brief delay to show success state
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch {
      toast.error('Failed to send change order to client');
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);

  // Get type label
  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      owner_requested: 'Owner Requested',
      unforeseen_condition: 'Unforeseen Condition',
      design_error: 'Design Error',
      scope_clarification: 'Scope Clarification',
      value_engineering: 'Value Engineering',
      allowance_adjustment: 'Allowance Adjustment',
    };
    return labels[type] || type;
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand-50 rounded-lg">
                <Mail className="h-5 w-5 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Send to Client</h2>
                <p className="text-sm text-gray-500">{changeOrder.coNumber}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Success State */}
          {sendSuccess ? (
            <div className="px-6 py-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Change Order Sent!</h3>
              <p className="text-sm text-gray-600">
                The change order has been sent to the client for approval.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Content */}
              <div className="px-6 py-5 space-y-5">
                {/* CO Summary */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <p className="font-medium text-gray-900">{getTypeLabel(changeOrder.type)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">
                        {changeOrder.pricing.isCredit ? 'Credit:' : 'Amount:'}
                      </span>
                      <p
                        className={`font-medium ${changeOrder.pricing.isCredit ? 'text-green-600' : 'text-gray-900'}`}
                      >
                        {changeOrder.pricing.isCredit ? '-' : ''}
                        {formatCurrency(changeOrder.pricing.totalAmount)}
                      </p>
                    </div>
                  </div>
                  {changeOrder.scheduleImpact?.hasImpact && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-gray-500 text-sm">Schedule Impact:</span>
                      <p className="font-medium text-amber-600 text-sm">
                        +{changeOrder.scheduleImpact.daysAdded} days
                      </p>
                    </div>
                  )}
                </div>

                {/* Recipient Email */}
                <div>
                  <label
                    htmlFor="recipientEmail"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Client Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="recipientEmail"
                    {...register('recipientEmail')}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                      errors.recipientEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="client@example.com"
                  />
                  {errors.recipientEmail && (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.recipientEmail.message}
                    </p>
                  )}
                </div>

                {/* CC */}
                <div>
                  <label htmlFor="cc" className="block text-sm font-medium text-gray-700 mb-1">
                    CC (optional)
                  </label>
                  <input
                    type="text"
                    id="cc"
                    {...register('cc')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <p className="mt-1 text-xs text-gray-500">Separate multiple emails with commas</p>
                </div>

                {/* Subject */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    {...register('subject')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                  {errors.subject && (
                    <p className="mt-1 text-sm text-red-500">{errors.subject.message}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Personal Message (optional)
                  </label>
                  <textarea
                    id="message"
                    {...register('message')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                    placeholder="Add a personal message to the client..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
                  )}
                </div>

                {/* Include Attachments */}
                <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Paperclip className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Include Attachments</p>
                      <p className="text-xs text-gray-500">
                        Include PDF and any attached documents
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('includeAttachments')}
                      className="sr-only peer"
                      defaultChecked
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-600"></div>
                  </label>
                </div>

                {/* Note about client portal */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <p className="text-sm text-blue-800">
                    The client will receive a link to review and approve or reject this change
                    order. They can also negotiate a counter-offer through the portal.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3 rounded-b-2xl">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Send to Client</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

export default SendChangeOrderModal;
